"use client";

/**
 * `RepeaterField` — array-of-subforms control. The persisted form-state
 * value is `Record<string, unknown>[]` whose row shape is dictated by
 * the nested `field.fields` config. We use rhf's `useFieldArray` to own
 * the array surface (append, remove, move) — that hook keeps the keyed
 * row identity stable across reorders so React doesn't unmount inputs
 * the user is mid-typing.
 *
 * Each row renders the nested fields through the same {@link FieldRenderer}
 * the top-level form uses, but with a `pathPrefix` of
 * `${field.name}.${rowIndex}`. Field components compute their full rhf
 * binding path as `${pathPrefix}.${field.name}` so a nested `pib` field
 * inside `partners[2]` registers under the canonical rhf path
 * `partners.2.pib`. That keeps zod's `z.array(z.object({...}))` schema
 * shape, the rhf field registry, and the persisted JSON payload aligned
 * — there's no special "inside a repeater" state to serialize.
 *
 * Row chrome:
 * - Header: row index (1-based for humans) + a remove button (Trash2).
 * - Body: the nested fields, vertically stacked.
 * - Reorder controls: ChevronUp / ChevronDown buttons on the row header.
 *   We deliberately use up/down arrows rather than full drag-and-drop
 *   for v0.1 — DnD is Plan UI-5, but reorder is a real need today
 *   (reordering invoice lines, moving a primary contact to the top).
 * - Disabled-state semantics on the arrows: the up arrow on the first
 *   row and the down arrow on the last row are disabled. There's
 *   nowhere for those rows to move and clicking the disabled button is
 *   a no-op rather than wrapping (no "move first row to bottom" — that
 *   would be surprising).
 *
 * Add affordance: a single "Add" button with a Plus icon at the bottom.
 * It calls `append({})` so the new row's nested fields hydrate from
 * their own per-field defaults (rhf treats undefined nested keys as
 * "uncontrolled" and the field components register defaults via their
 * own rhf register hooks). Concrete defaults per row could be added by
 * extending {@link RepeaterFieldDef} with an `emptyRow` factory; we don't
 * need that yet.
 *
 * `initialRows` lets a caller seed N empty rows on first mount when no
 * persisted value exists. We only seed when `useFieldArray.fields` is
 * empty AND `initialRows > 0`. Re-seeding on every mount would wipe
 * autosaved drafts — the rhf instance hydrates from `defaultValues`
 * upstream, and we trust that hydration before adding our own rows.
 *
 * Accessibility: each row's controls have `aria-label`s naming the
 * field's label and the row's 1-based index ("Remove Line items 2",
 * "Move Line items 3 up"). The label-and-index naming pattern matches
 * what GitHub, Linear, and Stripe Dashboard use for their list-item
 * controls and is the most discoverable for screen-reader users.
 */

import * as React from "react";
import { useFieldArray } from "react-hook-form";
import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";
import { Button } from "@aio/platform-ui";
import { useFormContext } from "../form-context";
import type { RepeaterFieldDef } from "../types";
import { FieldRenderer } from "../field-renderer";
import { resolveFieldPath } from "./path";

export interface RepeaterFieldProps {
  field: RepeaterFieldDef;
  /**
   * Repeaters can themselves nest inside another repeater, so we accept
   * a path prefix and chain it. A repeater named `addresses` inside row
   * `partners[2]` resolves its own path to `partners.2.addresses`.
   */
  pathPrefix?: string;
}

export function RepeaterField({
  field,
  pathPrefix,
}: RepeaterFieldProps): React.ReactElement | null {
  const { form } = useFormContext();

  if (field.condition && !field.condition(form.getValues())) {
    return null;
  }

  const path = resolveFieldPath(field.name, pathPrefix);
  const { fields: rows, append, remove, move } = useFieldArray({
    control: form.control,
    name: path,
  });

  // Seed initial rows on first mount when there's nothing persisted.
  // The empty-array check matters: we MUST NOT clobber a hydrated draft.
  // The `seededRef` guard prevents the seeding effect from firing more
  // than once per mount (otherwise rapid re-renders during hydration
  // could append duplicate rows).
  const seededRef = React.useRef(false);
  React.useEffect(() => {
    if (seededRef.current) return;
    if (rows.length > 0) {
      seededRef.current = true;
      return;
    }
    if (typeof field.initialRows === "number" && field.initialRows > 0) {
      seededRef.current = true;
      // `append` accepts an array to enqueue multiple rows in a single
      // form-state update — looping with N synchronous calls would
      // collapse to a single row because each call captures the same
      // pre-update state inside the same render pass.
      const seedRows = Array.from({ length: field.initialRows }, () => ({}));
      append(seedRows);
    }
    // Intentionally only run once on mount — we explicitly don't want
    // this effect re-running when rows length changes from a user action.
    // The `seededRef` guard above plus a one-shot dependency tuple keeps
    // the side effect bounded.
  }, [append, field.initialRows, rows.length]);

  return (
    <div className="space-y-3">
      <div>
        <span className="text-sm font-medium leading-none text-fg">
          {field.label}
        </span>
        {field.description ? (
          <p className="mt-1 text-xs text-fg-muted">{field.description}</p>
        ) : null}
      </div>

      <div className="space-y-3">
        {rows.map((row, index) => {
          const rowPath = `${path}.${String(index)}`;
          const humanIndex = index + 1;
          const isFirst = index === 0;
          const isLast = index === rows.length - 1;
          return (
            <div
              key={row.id}
              className="space-y-3 rounded-md border border-border bg-bg-subtle p-4"
              data-testid={`repeater-row-${String(index)}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
                  {field.label} {String(humanIndex)}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => {
                      move(index, index - 1);
                    }}
                    disabled={isFirst}
                    aria-label={`Move ${field.label} ${String(humanIndex)} up`}
                    className="inline-flex size-8 items-center justify-center rounded-md text-fg-muted hover:bg-bg hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronUp aria-hidden="true" className="size-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      move(index, index + 1);
                    }}
                    disabled={isLast}
                    aria-label={`Move ${field.label} ${String(humanIndex)} down`}
                    className="inline-flex size-8 items-center justify-center rounded-md text-fg-muted hover:bg-bg hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronDown aria-hidden="true" className="size-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      remove(index);
                    }}
                    aria-label={`Remove ${field.label} ${String(humanIndex)}`}
                    className="inline-flex size-8 items-center justify-center rounded-md text-fg-muted hover:bg-bg hover:text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <Trash2 aria-hidden="true" className="size-4" />
                  </button>
                </div>
              </div>
              <div className="space-y-3">
                {field.fields.map((nested) => (
                  <FieldRenderer
                    key={nested.name}
                    field={nested}
                    pathPrefix={rowPath}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <Button
        type="button"
        variant="outline"
        onClick={() => {
          append({});
        }}
        aria-label={`Add ${field.label}`}
        {...(field.disabled ? { disabled: true } : {})}
      >
        <Plus aria-hidden="true" className="mr-2 size-4" />
        Add {field.label}
      </Button>
    </div>
  );
}
